import { 
  Droplet,
  Package, 
  Zap, 
  Droplets, 
  FireExtinguisher,
  Shield, 
  Building, 
  House, 
  Eye, 
  Wrench,
  Flame,
  Wind,
  Fence,
  TreeDeciduous,
  LucideIcon,
  CircuitBoard,
  PipetteIcon,
  Mountain,
  Snowflake,
  Tags,
} from "lucide-react";

/**
 * System-level asset category definitions
 * 
 * This is a reference table for styling purposes only.
 * The actual category value is stored as a TEXT field on the asset.
 * This allows flexibility while maintaining consistent UI styling.
 */

export interface SystemAssetCategory {
  name: string;
  icon: LucideIcon;
  /** Lucide export name; stable for API/persistence (do not use `icon.name`). */
  iconName: string;
  color: string;
  description?: string;
}

export const SYSTEM_ASSET_CATEGORIES: Record<string, SystemAssetCategory> = {
  // Core categories
  'Mechanical': {
    name: 'Mechanical',
    icon: Wrench,
    iconName: 'Wrench',
    color: '#3B82F6', // blue-500
    description: 'HVAC, heating, cooling systems',
  },
  'Electrical': {
    name: 'Electrical',
    icon: Zap,
    iconName: 'Zap',
    color: '#EAB308', // yellow-500
    description: 'Electrical panels, lighting, circuits',
  },
  'Fire Protection': {
    name: 'Fire Protection',
    icon: FireExtinguisher,
    iconName: 'FireExtinguisher',
    color: '#DC2626', // red-600
    description: 'Sprinklers, alarms, suppression',
  },
  'Roof': {
    name: 'Roof',
    icon: House,
    iconName: 'House',
    color: '#F97316', // orange-500
    description: 'Roofing systems and components',
  },
  'Exterior': {
    name: 'Exterior',
    icon: Mountain,
    iconName: 'Mountain',
    color: '#8B5CF6', // violet-500
    description: 'External building components',
  },
  'Plumbing': {
    name: 'Plumbing',
    icon: Droplet,
    iconName: 'Droplet',
    color: '#06B6D4', // cyan-500
    description: 'Water supply and drainage',
  },
  'Water Support': {
    name: 'Water Support',
    icon: Droplets,
    iconName: 'Droplets',
    color: '#06B6D4', // cyan-500
    description: 'Water support systems',
  },
  'Water Supply & Plumbing': {
    name: 'Water Supply & Plumbing',
    icon: PipetteIcon,
    iconName: 'PipetteIcon',
    color: '#0EA5E9', // sky-500
    description: 'Water supply systems',
  },
  'Gas': {
    name: 'Gas',
    icon: Flame,
    iconName: 'Flame',
    color: '#EF4444', // red-500
    description: 'Gas lines and equipment',
  },
  'HVAC': {
    name: 'HVAC',
    icon: Wind,
    iconName: 'Wind',
    color: '#10B981', // emerald-500
    description: 'Heating, ventilation, air conditioning',
  },
  'Interior': {
    name: 'Interior',
    icon: Package,
    iconName: 'Package',
    color: '#A855F7', // purple-500
    description: 'Interior building components',
  },
  'Refrigeration': {
    name: 'Refrigeration',
    icon: Snowflake,
    iconName: 'Snowflake',
    color: '#3B82F6', // blue-500
    description: 'Refrigeration equipment and systems',
  },
  'Custom': {
    name: 'Custom',
    icon: Tags,
    iconName: 'Tags',
    color: '#6B7280', // gray-500
    description: 'Custom category',
  },
  'Site': {
    name: 'Site',
    icon: TreeDeciduous,
    iconName: 'TreeDeciduous',
    color: '#22C55E', // green-500
    description: 'Site and landscape elements',
  },
  'Safety': {
    name: 'Safety',
    icon: Shield,
    iconName: 'Shield',
    color: '#14B8A6', // teal-500
    description: 'Safety and security systems',
  },
  'Above Ceiling': {
    name: 'Above Ceiling',
    icon: Eye,
    iconName: 'Eye',
    color: '#6B7280', // gray-500
    description: 'Above ceiling infrastructure',
  },
  'Structural': {
    name: 'Structural',
    icon: Building,
    iconName: 'Building',
    color: '#78716C', // stone-500
    description: 'Structural building elements',
  },
  'Controls': {
    name: 'Controls',
    icon: CircuitBoard,
    iconName: 'CircuitBoard',
    color: '#EC4899', // pink-500
    description: 'Building automation and controls',
  },
  'Fencing': {
    name: 'Fencing',
    icon: Fence,
    iconName: 'Fence',
    color: '#84CC16', // lime-500
    description: 'Fencing and barriers',
  },
};

// Default category for unknown/unassigned
export const DEFAULT_CATEGORY: SystemAssetCategory = {
  name: 'Uncategorized',
  icon: Package,
  iconName: 'Package',
  color: '#6B7280', // gray-500
  description: 'Uncategorized assets',
};

/**
 * Get category styling by name (case-insensitive)
 */
export function getCategoryStyle(categoryName: string | null | undefined): SystemAssetCategory {
  if (!categoryName) return DEFAULT_CATEGORY;
  
  // Try exact match first
  if (SYSTEM_ASSET_CATEGORIES[categoryName]) {
    return SYSTEM_ASSET_CATEGORIES[categoryName];
  }
  
  // Try case-insensitive match
  const lowerName = categoryName.toLowerCase();
  const match = Object.entries(SYSTEM_ASSET_CATEGORIES).find(
    ([key]) => key.toLowerCase() === lowerName
  );
  
  if (match) {
    return match[1];
  }
  
  // Return default with the original name preserved
  return {
    ...DEFAULT_CATEGORY,
    name: categoryName,
  };
}

/**
 * Get all category names for dropdowns/filters
 */
export function getAllCategoryNames(): string[] {
  return Object.keys(SYSTEM_ASSET_CATEGORIES);
}

/** API `system_type` slug order for Create System (icons/colors from SYSTEM_ASSET_CATEGORIES). */
export const CREATE_SYSTEM_TYPE_ORDER = [
  'electrical',
  'fire_protection',
  'gas',
  'water_support',
  'plumbing',
  'roof',
  'interior',
  'refrigeration',
  'exterior',
  'mechanical',
  'hvac',
  'custom',
] as const;

export type CreateSystemTypeValue = (typeof CREATE_SYSTEM_TYPE_ORDER)[number];

export function systemTypeSlugToCategoryKey(slug: string): string {
  if (slug === 'hvac') return 'HVAC';
  return slug
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function toRomanNumeral(n: number): string {
  const pairs: readonly [number, string][] = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];
  let num = n;
  let result = '';
  for (const [value, symbol] of pairs) {
    while (num >= value) {
      result += symbol;
      num -= value;
    }
  }
  return result;
}

export type CreateSystemTypeRow = {
  value: string;
  label: string;
  icon: LucideIcon;
  /** Stable Lucide export name for API/persistence (not `icon.name`). */
  iconName: string;
  color: string;
};

/**
 * Rows for the Create System type picker: same icon/color as SYSTEM_ASSET_CATEGORIES.
 */
export function getCreateSystemTypeRows(): CreateSystemTypeRow[] {
  return CREATE_SYSTEM_TYPE_ORDER.map((value, index) => {
    const key = systemTypeSlugToCategoryKey(value);
    const style = SYSTEM_ASSET_CATEGORIES[key] ?? DEFAULT_CATEGORY;
    const label =
      value === 'custom'
        ? style.name
        : `${toRomanNumeral(index + 1)}. ${style.name}`;
    return {
      value,
      label,
      icon: style.icon,
      iconName: style.iconName,
      color: style.color,
    };
  });
}

